import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

const buttonVariants = cva(
  "cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-[var(--rr-red)] text-white font-semibold shadow-[var(--shadow-sm)] hover:bg-[var(--rr-red-hover)] hover:shadow-[var(--shadow-md)] transition-all duration-150",
        destructive:
          "bg-[var(--rr-error)] text-white font-semibold hover:bg-[var(--rr-error-hover)] focus-visible:ring-destructive/20",
        outline:
          "border-[1.5px] border-[var(--border-strong)] bg-transparent text-[var(--text-primary)] font-medium hover:border-[var(--rr-red)] hover:text-[var(--text-brand)] hover:bg-transparent transition-all duration-150",
        secondary:
          "bg-[var(--surface-muted)] text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]",
        ghost:
          "text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]",
        link: "text-[var(--text-brand)] underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-5 py-2.5 has-[>svg]:px-4 rounded-lg",
        sm: "h-8 rounded-lg gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-11 rounded-lg px-6 has-[>svg]:px-5",
        icon: "size-9 rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

// forwardRef (2026-10-20): React 18 drops `ref` on a plain function component, so a Radix trigger
// (DropdownMenuTrigger / PopoverTrigger asChild) around a Button never got the element and its menu
// opened off-screen (the Jobs table "More actions" menu on staging, and the Messages composer's
// attachment menu). Passing the ref on fixes every such trigger at once.
const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
    }
>(function Button({ className, variant, size, asChild = false, ...props }, ref) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
});

export { Button, buttonVariants };
